/**
 * Which file on a job is probably the signed work order — a SUGGESTION only
 * (threads/wo-in-wrong-slot/mockup.html).
 *
 * On 2026-09-30, 15 held packets had the signed WO sitting on the job as a
 * Document or a Closeout upload, where the packet hold never looks. Pointing at
 * the likely file turns "find it, download it, upload it again" into one click
 * — but a file name is a hint, not proof of what's on the page, so nothing
 * moves until a person confirms, and the server refuses anything that isn't a
 * PDF by its bytes.
 */

const IMAGE_EXT = /\.(jpe?g|png|heic|heif|gif|webp|tiff?)$/i;

/** Digits only — "WO#8000214158" and "8000214158" compare equal. */
function digits(s: string): string {
    return s.replace(/\D/g, "");
}

/** Signed / completed copies — the strongest hint. */
function saysSigned(name: string): boolean {
    return /\bsigned\b|\bcompleted\b/i.test(name.replace(/[_-]+/g, " "));
}

/** "WO", "W.O", "work order", or this job's WO number anywhere in the name. */
export function looksLikeWorkOrder(fileName: string | null | undefined, woNumber?: string | null): boolean {
    const name = (fileName ?? "").trim();
    if (!name || IMAGE_EXT.test(name)) return false;
    const spaced = name.replace(/[_-]+/g, " ");
    if (saysSigned(spaced) && /\bw\.?\s?o\b|work\s*order/i.test(spaced)) return true;
    if (/(^|[^a-z])w\.?\s?o(?![a-z])/i.test(spaced) || /work\s*order/i.test(spaced)) return true;
    const wo = digits(woNumber ?? "");
    return wo.length >= 5 && digits(name).includes(wo);
}

/**
 * A file that reads as a work order: its name says so, or the upload checker
 * read its words or its form as one and saved that verdict (trust audit
 * Phase 1; `job_attachments.wo_verdict_reason`).
 */
export function isWorkOrderLooking(
    file: { file_name: string | null | undefined; wo_verdict_reason?: string | null },
    woNumber?: string | null,
): boolean {
    return !!file.wo_verdict_reason || looksLikeWorkOrder(file.file_name, woNumber);
}

/**
 * The file to suggest, or null. Signed/completed copies beat a bare "WO" name
 * (which is often the original from the vendor), then newest first.
 */
export function likelySignedWorkOrder<
    T extends { file_name: string | null; created_at: string; wo_verdict_reason?: string | null },
>(files: T[], woNumber?: string | null): T | null {
    const candidates = files.filter((f) => isWorkOrderLooking(f, woNumber));
    if (candidates.length === 0) return null;
    const rank = (f: T) => (saysSigned(f.file_name ?? "") ? 1 : 0);
    return [...candidates].sort((a, b) => rank(b) - rank(a) || (a.created_at < b.created_at ? 1 : -1))[0] ?? null;
}

export interface PacketStatusLike {
    held: boolean;
    waiting_on_reports: boolean;
    waiting_on_work_order: boolean;
    reports?: { filed: number; total: number };
    /** Every address on the held packet is ours (#1759): it never goes by itself. */
    no_customer_email?: boolean;
}

/**
 * The held-packet banner on the job page, naming what it is ACTUALLY waiting
 * on. It used to read "Awaiting official report" on every held job, including
 * the 37 whose reports were all in and only the work order was missing.
 */
export function heldPacketLine(s: PacketStatusLike | null | undefined): string | null {
    if (!s?.held) return null;
    if (s.no_customer_email) return "Packet held — no customer email on file. It won't go out by itself.";
    const filed = s.reports?.filed ?? 0;
    const total = s.reports?.total ?? 0;
    if (s.waiting_on_reports && s.waiting_on_work_order) {
        return `Packet held — waiting on official reports (${filed} of ${total} filed) and the completed work order.`;
    }
    if (s.waiting_on_work_order) return "Packet held — waiting on the completed work order. Reports are all in.";
    if (s.waiting_on_reports) {
        return `Awaiting official report — ${filed} of ${total} filed. The customer packet releases automatically when every device is filed.`;
    }
    return "Packet held — nothing is missing now. It goes out on its own within the hour.";
}
